import {
  LoaderStreamChunkSchema,
  type LoaderAssetsPayload,
  type LoaderStreamChunk,
  type LoaderTerminalChunk,
} from "@bestowinc/tinkertoy-core";

import type { ModuleLoader } from "../module-loader";
import type { ViewLoader } from "../view-loader";

import { primeLoaderAssets } from "./prime-loader-assets";

export type ConsumeLoaderStreamResult = {
  terminal: LoaderTerminalChunk;
  assetsReceivedMs?: number;
};

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw signal.reason ?? new DOMException("Aborted", "AbortError");
  }
}

async function* readNdjsonLines(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
): AsyncGenerator<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const abortReader = (): void => {
    void reader.cancel();
  };
  signal.addEventListener("abort", abortReader);

  try {
    while (true) {
      throwIfAborted(signal);
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });

      let newlineIndex = buffer.indexOf("\n");
      while (newlineIndex !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);
        if (line.length > 0) {
          yield line;
        }
        newlineIndex = buffer.indexOf("\n");
      }
    }

    buffer += decoder.decode();

    const trailing = buffer.trim();
    if (trailing.length > 0) {
      yield trailing;
    }
  } finally {
    signal.removeEventListener("abort", abortReader);
    reader.releaseLock();
  }
}

function parseStreamChunk(line: string): LoaderStreamChunk {
  let json: unknown;
  try {
    json = JSON.parse(line);
  } catch {
    throw new Error("Malformed NDJSON record in loader stream.");
  }

  const parsed = LoaderStreamChunkSchema.safeParse(json);
  if (!parsed.success) {
    throw new Error("Unexpected loader stream record shape.");
  }

  return parsed.data;
}

/**
 * Consumes a negotiated NDJSON `/load` response: primes CDN imports on each
 * assets chunk (and hands it to `onAssets`), resolving once the terminal
 * chunk arrives.
 */
export async function consumeLoaderStream(
  response: Response,
  signal: AbortSignal,
  loaders: {
    viewLoader: ViewLoader;
    moduleLoader: ModuleLoader;
  },
  startedAt: number,
  onAssets?: (payload: LoaderAssetsPayload) => void,
): Promise<ConsumeLoaderStreamResult> {
  if (response.body === null) {
    throw new Error("Loader stream response has no body.");
  }

  let assetsReceivedMs: number | undefined;
  let terminal: LoaderTerminalChunk | undefined;

  for await (const line of readNdjsonLines(response.body, signal)) {
    throwIfAborted(signal);
    const chunk = parseStreamChunk(line);

    if (chunk.type === "assets") {
      if (assetsReceivedMs === undefined) {
        assetsReceivedMs = Math.max(0, performance.now() - startedAt);
      }
      primeLoaderAssets(chunk.payload, loaders);
      onAssets?.(chunk.payload);
      continue;
    }

    terminal = chunk;
    break;
  }

  if (terminal === undefined) {
    throwIfAborted(signal);
    throw new Error("Loader stream ended without a terminal chunk.");
  }

  return { terminal, assetsReceivedMs };
}
